import { useCallback, useRef } from 'react';
import { useNotification } from '../context/NotificationContext';
import { AxiosError } from 'axios';

export const useErrorHandler = () => {
  const { showNotification } = useNotification();
  // Keep track of the last error to prevent duplicates
  const lastErrorRef = useRef<string>('');
  const lastErrorTimeRef = useRef<number>(0);

  const handleError = useCallback((error: unknown) => {
    console.error('Error:', error);
    
    let errorMessage = 'An unexpected error occurred. Please try again.';
    
    if (error instanceof AxiosError) {
      const apiError = error.response?.data as any;
      
      if (apiError?.error) {
        errorMessage = `${apiError.error}${apiError.details ? `: ${apiError.details}` : ''}`;
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Network error: Unable to connect to the server. Please check your connection and try again.';
      } else if (error.message && error.message.includes('CORS')) {
        errorMessage = 'CORS error: The server is not allowing cross-origin requests. Please check the server configuration.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Resource not found. Please check the URL and try again.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in and try again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to perform this action.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}`;
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    
    // Prevent showing the same error message multiple times in a short period
    const now = Date.now();
    if (lastErrorRef.current !== errorMessage || (now - lastErrorTimeRef.current) > 5000) {
      showNotification(errorMessage, 'error');
      lastErrorRef.current = errorMessage;
      lastErrorTimeRef.current = now;
    }
  }, [showNotification]);

  return { handleError };
};
